import { gql, useMutation } from '@apollo/client'
import {
  Check as AcknowledgeIcon,
  Close as CloseIcon,
  ArrowUpward as EscalateIcon,
  SwapHoriz as ReassignIcon,
} from '@mui/icons-material'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import { Theme } from '@mui/material/styles'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { DateTime } from 'luxon'
import p from 'prop-types'
import React, { ReactNode, useState } from 'react'
import ReactGA from 'react-ga4'
import { Alert, AlertStatus, EscalationPolicyStep } from '../../../schema'
import AlertCloseDialog, {
  ResolutionMetadata,
} from './AlertCloseDialog'
import AlertReassignDialog from './AlertReassignDialog'
import CardActions from '../../details/CardActions'
import { Notice } from '../../details/Notices'
import { renderChipsDest } from '../../escalation-policies/stepUtil'
import { ServiceLink } from '../../links'
import LoadingButton from '../../loading/components/LoadingButton'
import ServiceNotices from '../../services/ServiceNotices'
import { styles as globalStyles } from '../../styles/materialStyles'
import AppLink from '../../util/AppLink'
import Markdown from '../../util/Markdown'
import { useConfigValue } from '../../util/RequireConfig'
import { Time } from '../../util/Time'
import { useIsWidthDown } from '../../util/useWidth'
import AlertDetailLogs from '../AlertDetailLogs'
import AlertComments from './AlertComments'
import AlertFeedback, {
  mutation as undoFeedbackMutation,
} from './AlertFeedback'
interface AlertDetailsProps {
  data: Alert
}

interface EscalationPolicyInfo {
  repeatCount?: number
  repeat?: number
  numSteps?: number
  steps?: EscalationPolicyStep[]
  status: AlertStatus
  currentLevel?: number
  lastEscalation?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  card: globalStyles(theme).card,
  cardContainer: globalStyles(theme).cardContainer,
  cardFull: globalStyles(theme).cardFull,
  tableCardContent: globalStyles(theme).tableCardContent,
  epHeader: {
    paddingBottom: 8,
  },
  threadContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    padding: '1rem',
    backgroundColor: theme.palette.background.default,
  },
  messageDivider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: '1rem',
  },
}))

const localStorage = window.localStorage
const exactTimesKey = 'show_exact_times'

const updateStatusMutation = gql`
  mutation UpdateAlertsMutation($input: UpdateAlertsInput!) {
    updateAlerts(input: $input) {
      id
    }
  }
`

export default function AlertDetails(
  props: AlertDetailsProps,
): React.JSX.Element {
  const [analyticsID] = useConfigValue('General.GoogleAnalyticsID') as [string]
  const classes = useStyles()
  const isMobile = useIsWidthDown('sm')

  const alertAction = (action: string, mutation: () => void): void => {
    if (analyticsID) ReactGA.event({ category: 'Alert Action', action })
    mutation()
  }

  const [undoFeedback, undoFeedbackStatus] = useMutation(undoFeedbackMutation, {
    variables: {
      input: {
        alertID: props.data.id,
        noiseReason: '',
      },
    },
  })

  const [ack] = useMutation(updateStatusMutation, {
    variables: {
      input: {
        alertIDs: [props.data.id],
        newStatus: 'StatusAcknowledged',
      },
    },
  })
  const [close, closeStatus] = useMutation(updateStatusMutation)
  const [escalate] = useMutation(
    gql`
      mutation EscalateAlertMutation($input: [Int!]) {
        escalateAlerts(input: $input) {
          id
        }
      }
    `,
    {
      variables: {
        input: [props.data.id],
      },
    },
  )

  // localstorage stores true/false as a string; convert to a bool
  // default to true if localstorage is not set
  let _showExactTimes = localStorage.getItem(exactTimesKey) || false
  if (typeof _showExactTimes !== 'boolean') {
    _showExactTimes = _showExactTimes === 'true'
  }

  const [fullDescription, setFullDescription] = useState(false)
  const [showExactTimes, setShowExactTimes] = useState(_showExactTimes)
  const [closeDialogOpen, setCloseDialogOpen] = useState(false)
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false)

  const [reassign, reassignStatus] = useMutation(updateStatusMutation)

  /*
   * Update state and local storage with new boolean value
   * telling whether or not the show exact times toggle is active
   */
  function handleToggleExactTimes(): void {
    const newVal = !showExactTimes
    setShowExactTimes(newVal)
    localStorage.setItem(exactTimesKey, newVal.toString())
  }

  /*
   * Returns properties from the escalation policy
   * for easier use in functions.
   */
  function epsHelper(): EscalationPolicyInfo {
    const ep = props.data?.service?.escalationPolicy
    const alert = props.data
    const state = props.data.state

    return {
      repeatCount: state?.repeatCount,
      repeat: ep?.repeat,
      numSteps: ep?.steps.length,
      steps: ep?.steps,
      status: alert.status,
      currentLevel: state?.stepNumber,
      lastEscalation: state?.lastEscalation,
    }
  }

  function canAutoEscalate(): boolean {
    const { currentLevel, status, steps, repeat, repeatCount } = epsHelper()

    if (status !== 'StatusUnacknowledged') {
      return false
    }

    if (
      currentLevel === (steps?.length ?? 0) - 1 &&
      (repeatCount ?? 0) >= (repeat ?? 0)
    ) {
      return false
    }

    return true
  }

  function getNextEscalation(): React.JSX.Element | string {
    const { currentLevel, lastEscalation, steps } = epsHelper()
    if (!canAutoEscalate()) return 'None'

    const prevEscalation = DateTime.fromISO(lastEscalation ?? '')
    const nextEsclation = prevEscalation.plus({
      minutes: steps ? steps[currentLevel ?? 0].delayMinutes : 0,
    })

    return (
      <Time
        time={nextEsclation}
        format='relative'
        units={['hours', 'minutes', 'seconds']}
        precise
      />
    )
  }

  function renderEscalationPolicySteps(): React.JSX.Element[] | JSX.Element {
    const { steps, status, currentLevel } = epsHelper()

    if (!steps?.length) {
      return (
        <TableRow>
          <TableCell>No steps</TableCell>
          <TableCell>&mdash;</TableCell>
          <TableCell>&mdash;</TableCell>
        </TableRow>
      )
    }

    return steps.map((step, index) => {
      const selected =
        status !== 'StatusClosed' &&
        (currentLevel ?? 0) % steps.length === index

      return (
        <TableRow key={index} selected={selected}>
          <TableCell>Step #{index + 1}</TableCell>
          <TableCell>{renderChipsDest(step.actions)}</TableCell>
        </TableRow>
      )
    })
  }

  function renderAlertDetails(): ReactNode {
    const alert = props.data
    let details = (alert.details || '').trim()
    if (!details) return null

    if (!fullDescription && details.length > 1000) {
      details = details.slice(0, 1000).trim() + ' ...'
    }

    let expandTextAction = null
    if (details.length > 1000) {
      let text = 'Show Less'

      if (!fullDescription) {
        text = 'Show More'
      }

      expandTextAction = (
        <Typography
          color='textSecondary'
          onClick={() => setFullDescription(!fullDescription)}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            justifyContent: 'center',
            textAlign: 'center',
            paddingTop: '1em',
          }}
        >
          {text}
        </Typography>
      )
    }

    return (
      <Grid
        item
        xs={12}
        data-cy='alert-details'
        className={classes.cardContainer}
      >
        <Card sx={{ width: '100%' }}>
          <CardContent>
            <Typography component='h3' variant='h5'>
              Details
            </Typography>
            <Typography
              variant='body1'
              component='div'
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowX: 'auto',
              }}
            >
              <Markdown value={details + '\n'} />
            </Typography>
            {expandTextAction}
          </CardContent>
        </Card>
      </Grid>
    )
  }

  /*
   * Options to show for alert details menu
   */
  function getMenuOptions(): Array<JSX.Element> {
    const { status } = props.data
    if (status === 'StatusClosed') return []
    const isMaintMode = Boolean(props.data?.service?.maintenanceExpiresAt)

    return [
      <ButtonGroup
        key='update-alert-buttons'
        variant='contained'
        aria-label='Update Alert Status Button Group'
      >
        {status === 'StatusUnacknowledged' && (
          <Button
            startIcon={<AcknowledgeIcon />}
            onClick={() => alertAction('alert_acknowledged', ack)}
          >
            Acknowledge
          </Button>
        )}
        <Button
          startIcon={<EscalateIcon />}
          onClick={() => alertAction('alert_escalated', escalate)}
          disabled={isMaintMode}
        >
          Escalate
        </Button>
        <Button
          startIcon={<ReassignIcon />}
          onClick={() => setReassignDialogOpen(true)}
        >
          Reassign
        </Button>
        <Button
          startIcon={<CloseIcon />}
          onClick={() => setCloseDialogOpen(true)}
        >
          Close
        </Button>
      </ButtonGroup>,
    ]
  }

  const { data: alert } = props

  let extraNotices: Notice[] = alert.pendingNotifications.map((n) => ({
    type: 'WARNING',
    message: `Notification Pending for ${n.destination}`,
    details:
      'This could be due to rate-limiting, processing, or network delays.',
  }))

  const noiseReason = alert?.noiseReason ?? ''
  if (noiseReason !== '') {
    const nrArr = noiseReason.split('|')
    const reasons = nrArr.join(', ')
    extraNotices = [
      ...extraNotices,
      {
        type: 'INFO',
        message: 'This alert has been marked as noise',
        details: `Reason${nrArr.length > 1 ? 's' : ''}: ${reasons}`,
        action: (
          <LoadingButton
            buttonText='Undo'
            aria-label='Reset noise reasons'
            variant='text'
            loading={undoFeedbackStatus.called && undoFeedbackStatus.loading}
            onClick={() => undoFeedback()}
          />
        ),
      },
    ]
  }

  return (
    <Grid container spacing={2}>
      <ServiceNotices
        serviceID={alert?.service?.id ?? ''}
        extraNotices={extraNotices as Notice[]}
      />

      {/* Main Alert Info */}
      <Grid
        item
        lg={isMobile || noiseReason !== '' ? 12 : 8}
        className={classes.cardContainer}
      >
        <Card
          sx={{
            width: '100%',
            height: '100%',
            flexDirection: 'column',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <CardContent data-cy='alert-summary'>
            <Grid container spacing={1}>
              {alert.service && (
                <Grid item xs={12}>
                  <Typography variant='body1'>
                    {ServiceLink(alert.service)}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography component='h2' variant='h5'>
                  {alert.alertID}: {alert.summary}
                </Typography>
                <Chip
                  size='small'
                  label={alert.severity.toUpperCase()}
                  color={
                    alert.severity === 'CRITICAL'
                      ? 'error'
                      : alert.severity === 'HIGH'
                        ? 'warning'
                        : alert.severity === 'MEDIUM'
                          ? 'primary'
                          : 'default'
                  }
                  sx={{ mt: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body1' data-cy='alert-status'>
                  {alert.status.toUpperCase().replace('STATUS', '')}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions primaryActions={getMenuOptions()} />
        </Card>
      </Grid>
      {!noiseReason && (
        <Grid item xs={12} lg={isMobile ? 12 : 4}>
          <AlertFeedback alertID={alert.alertID} />
        </Grid>
      )}
      {renderAlertDetails()}

      {/* Resolution Metadata - Only show for closed alerts */}
      {alert.status === 'StatusClosed' &&
        alert.resolutionMetadata &&
        Object.keys(alert.resolutionMetadata).length > 0 && (
          <Grid item xs={12} className={classes.cardContainer}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Typography component='h3' variant='h5'>
                  Resolution Information
                </Typography>
                <Table>
                  <TableBody>
                    {Object.entries(alert.resolutionMetadata)
                      .filter(([key]) => key !== 'slack_conversation')
                      .map(([key, value]) => (
                        <TableRow key={key}>
                          <TableCell
                            component='th'
                            scope='row'
                            sx={{ fontWeight: 'bold', width: '30%' }}
                          >
                            {key
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </TableCell>
                          <TableCell>
                            <Markdown value={String(value)} />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                {/* Slack Conversation Section */}
                {alert.resolutionMetadata.slack_conversation && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Typography
                      component='h4'
                      variant='h6'
                      sx={{ marginBottom: '1rem' }}
                    >
                      Conversation Thread
                    </Typography>
                    <div
                      className={classes.threadContainer}
                    >
                      {(() => {
                        try {
                          interface ThreadMessage {
                            user?: string
                            user_name?: string
                            text?: string
                            ts?: string
                            thread_ts?: string
                          }

                          const threadMessages =
                            typeof alert.resolutionMetadata.slack_conversation ===
                              'string'
                              ? (JSON.parse(
                                alert.resolutionMetadata.slack_conversation,
                              ) as ThreadMessage[])
                              : (alert.resolutionMetadata
                                .slack_conversation as ThreadMessage[])

                          if (
                            !Array.isArray(threadMessages) ||
                            threadMessages.length === 0
                          ) {
                            return (
                              <Typography variant='body2' color='textSecondary'>
                                No thread messages
                              </Typography>
                            )
                          }

                          return threadMessages.map(
                            (msg: ThreadMessage, index: number) => (
                              <div key={index}>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '0.5rem',
                                  }}
                                >
                                  <Typography
                                    variant='body2'
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    {msg.user_name ||
                                      msg.user ||
                                      'Unknown User'}
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    color='textSecondary'
                                  >
                                    {msg.ts
                                      ? DateTime.fromSeconds(
                                        parseFloat(msg.ts),
                                      ).toFormat('MMM dd, yyyy HH:mm:ss')
                                      : ''}
                                  </Typography>
                                </div>
                                <Typography
                                  variant='body2'
                                  sx={{
                                    marginBottom: '1rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {msg.text || ''}
                                </Typography>
                                {index < threadMessages.length - 1 && (
                                  <div
                                    className={classes.messageDivider}
                                  />
                                )}
                              </div>
                            ),
                          )
                        } catch {
                          return (
                            <Typography variant='body2' color='error'>
                              Error parsing thread messages
                            </Typography>
                          )
                        }
                      })()}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

      {/* Escalation Policy Info */}
      <Grid item xs={12} className={classes.cardContainer}>
        <Card style={{ width: '100%', overflowX: 'auto' }}>
          <CardContent>
            <Typography
              className={classes.epHeader}
              component='h3'
              variant='h5'
            >
              <AppLink
                to={`/escalation-policies/${alert.service?.escalationPolicy?.id}`}
              >
                Escalation Policy
              </AppLink>
            </Typography>
            {alert?.state?.lastEscalation && (
              <React.Fragment>
                <Typography color='textSecondary' variant='caption'>
                  Last Escalated: <Time time={alert.state.lastEscalation} />
                </Typography>
                <br />
                <Typography color='textSecondary' variant='caption'>
                  Next Escalation: {getNextEscalation()}
                </Typography>
              </React.Fragment>
            )}
          </CardContent>
          <CardContent className={classes.tableCardContent}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Step</TableCell>
                  <TableCell>Alert</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderEscalationPolicySteps()}</TableBody>
            </Table>
          </CardContent>
          <CardContent>
            <Typography color='textSecondary' variant='caption'>
              Visit this escalation policy for more information.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Comments */}
      <AlertComments alertID={alert.alertID} />

      {/* Alert Logs */}
      <Grid item xs={12} className={classes.cardContainer}>
        <Card sx={{ width: '100%' }}>
          <div style={{ display: 'flex' }}>
            <CardContent style={{ flex: 1, paddingBottom: 0 }}>
              <Typography component='h3' variant='h5'>
                Event Log
              </Typography>
            </CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={showExactTimes}
                  onChange={handleToggleExactTimes}
                />
              }
              label='Full Timestamps'
              style={{ padding: '0.5em 0.5em 0 0' }}
            />
          </div>
          <CardContent
            className={classes.tableCardContent}
            style={{ paddingBottom: 0 }}
          >
            <AlertDetailLogs
              alertID={props.data.alertID}
              showExactTimes={showExactTimes}
            />
          </CardContent>
        </Card>
      </Grid>
      {/* Close Dialog */}
      <AlertCloseDialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        alert={{
          id: props.data.id,
          alertID: props.data.alertID,
          summary: props.data.summary,
        }}
        onSubmit={async (metadata: ResolutionMetadata) => {
          alertAction('alert_closed', async () => {
            await close({
              variables: {
                input: {
                  alertIDs: [props.data.id],
                  newStatus: 'StatusClosed',
                  resolutionMetadata: metadata,
                },
              },
            })
          })
          setCloseDialogOpen(false)
        }}
        loading={closeStatus.loading}
      />
      <AlertReassignDialog
        open={reassignDialogOpen}
        onClose={() => setReassignDialogOpen(false)}
        alert={{
          id: props.data.id,
          alertID: props.data.alertID,
          summary: props.data.summary,
        }}
        onSubmit={async (newServiceID: string) => {
          alertAction('alert_reassigned', async () => {
            await reassign({
              variables: {
                input: {
                  alertIDs: [props.data.id],
                  newServiceID,
                },
              },
            })
          })
          setReassignDialogOpen(false)
        }}
        loading={reassignStatus.loading}
      />
    </Grid>
  )
}

AlertDetails.propTypes = {
  error: p.shape({ message: p.string }),
}
